import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Edge,
  Node,
  OnEdgesChange,
  addEdge,
  useEdgesState,
  useNodesState,
} from "reactflow";
import { DateObject } from "react-multi-date-picker";

import {
  CreateCampaignRequestType,
  CreateCampaignResponseType,
  EditCampaignInfoRequestType,
  FindStepRequestType,
  FindStepResponseType,
  GetCampaignRequestType,
  GetCampaignResponseType,
  MainDetailsType,
  MoreDetailsType,
  NodeType,
  ResponseType,
} from "./SiCaTypes";

import useAxios from "../../services/useAxios";
import { apiResponseHandler, nodeTypeConvertToString } from "./SiCahelper";

interface CampaignContextType {
  mainDetails: MainDetailsType;
  moreDetails: MoreDetailsType;
  nodes: Node<NodeType>[];
  edges: Edge[];
  changeMainDetails: (details: MainDetailsType) => void;
  changeNodes: (nodes: Node<NodeType>[]) => void;
  changeEdges: (edges: Edge[]) => void;
  onEdgesChange: OnEdgesChange;
  submitDetails: React.FormEventHandler<HTMLFormElement>;
}

const CampaignContext = createContext<CampaignContextType>({
  mainDetails: {
    id: "",
    title: "",
    description: "",
    products: [],
    tags: [],
    starttm: "",
    endtm: "",
    status: "",
  },
  moreDetails: {
    stepsCount: 0,
    finishedStepsCount: 0,
    clueCount: 0,
    chanceCount: 0,
    sellCount: 0,
    sellAmount: 0,
  },
  nodes: [],
  edges: [],
  changeMainDetails: (details) => {},
  changeNodes: (nodes) => {},
  changeEdges: (edges) => {},
  onEdgesChange: (changes) => {},
  submitDetails: () => {},
});

const CampaignContextProvider: React.FC<{
  children: JSX.Element | JSX.Element[];
}> = ({ children }) => {
  const { campaignId } = useParams<{ campaignId: string }>();

  const { execute: getCampaignAPI } = useAxios("test/get_campaign");
  const { execute: createCampaignAPI } = useAxios("test/create_campaign");
  const { execute: editCampaignInfoAPI } = useAxios("api2/edit_campaign_info");
  const { execute: FindStepAPI } = useAxios("api/Find_Step");

  const nav = useNavigate();

  const [nodes, setNodes] = useNodesState<NodeType>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [mainDetails, setMainDetails] = useState<MainDetailsType>({
    id: "",
    title: "",
    description: "",
    products: [],
    tags: [],
    starttm: "",
    endtm: "",
    status: "",
  });

  // Separated more details, because we need to keep track of stepCounts and also we need to set
  // other details with data from the server. so I do this.
  const [theDetails, setTheDetails] = useState({
    finishedStepsCount: 0,
    clueCount: 0,
    chanceCount: 0,
    sellCount: 0,
    sellAmount: 0,
  });

  const moreDetails = useMemo<MoreDetailsType>(
    () => ({
      stepsCount: nodes.length,
      ...theDetails,
    }),
    [nodes, theDetails]
  );

  const changeNodes = useCallback(
    (nodes: React.SetStateAction<Node<NodeType, string | undefined>[]>) => {
      setNodes(nodes);
    },
    [setNodes]
  );

  const changeEdges = useCallback(
    (edges: React.SetStateAction<Edge<any>[]>) => {
      setEdges(edges);
    },
    [setEdges]
  );

  const changeMainDetails = useCallback(
    (details: React.SetStateAction<typeof mainDetails>) => {
      setMainDetails(details);
    },
    [setMainDetails]
  );

  const submitDetails = useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      // prevent refreshing page
      event.preventDefault();

      // set data to send to server.
      let data: CreateCampaignRequestType = {
        title: mainDetails.title,
        description: mainDetails.description,
        product_services: mainDetails.products.join(","),
        tags: mainDetails.tags.join(","),
        endtm: mainDetails.endtm,
        starttm: mainDetails.starttm,
        status: mainDetails.status,
      };

      if (mainDetails.id !== "") {
        // adding campaign id for edit api.
        let d: EditCampaignInfoRequestType = {
          campaign_id: mainDetails.id,
          ...data,
        };

        editCampaignInfoAPI(d).then((res: ResponseType) => {
          apiResponseHandler(res, {
            success: "کمپین با موفقیت ویرایش شد.",
            failure: "مشکلی در ویرایش کمپین به وجود آمده است.",
          });
        });
      } else {
        createCampaignAPI(data).then((res: CreateCampaignResponseType) => {
          apiResponseHandler(res, {
            success: "کمپین با موفقیت ایجاد شد.",
            failure: "مشکلی در ایجاد کمپین به وجود آمده است.",
          });

          if (+res.status === 1) {
            setTimeout(() => nav(`/single-campaign/${res.campaign_id}`), 2000);
          }
        });
      }
    },
    [mainDetails, nav, editCampaignInfoAPI, createCampaignAPI]
  );

  useEffect(() => {
    if (campaignId) {
      // getting campaign info
      const data1: GetCampaignRequestType = { campaignId: campaignId };
      getCampaignAPI(data1).then(
        ({ campaignInfo, ...res }: GetCampaignResponseType) => {
          setTheDetails({
            chanceCount: +res.chanceCount,
            clueCount: +res.leadscount,
            finishedStepsCount: +res.stepsTrueCount,
            sellAmount: +res.salesAmount,
            sellCount: +res.salesCount,
          });
          setMainDetails({
            id: campaignInfo.id,
            title: campaignInfo.title,
            description: campaignInfo.description,
            products: res.products.map((p) => "" + p.product_service_id),
            tags: res.tags.map((t) => "" + t.TagId),
            starttm: campaignInfo.starttm,
            endtm: campaignInfo.endtm,
            status: campaignInfo.status,
          });
        }
      );

      // getting steps
      const data2: FindStepRequestType = {
        conditions: `campaign_id/=/${campaignId}@@status/!=/0`,
      };
      FindStepAPI(data2).then((res: FindStepResponseType) => {
        let nodes: Node<NodeType>[] = [];
        let edges: Edge[] = [];

        for (let i = 0; i < res.length; i++) {
          nodes = nodes.concat({
            id: res[i].id,
            position: {
              x: +res[i].position_x,
              y: +res[i].position_y,
            },
            type: nodeTypeConvertToString(+res[i].type),
            data: {
              id: +res[i].id,
              title: res[i].title,
              description: res[i].description,
              time: +res[i].time,
              automate: +res[i].automate === 1,
              isActive: +res[i].status === 2,
              type: +res[i].type,
              text: res[i].text,
              trueLabel: res[i].true_label,
              falseLabel: res[i].false_label,
            },
          });

          if (+res[i].status_true !== 0) {
            edges = addEdge(
              {
                id: "" + (new DateObject().unix + i),
                source: res[i].id,
                sourceHandle: "true",
                target: res[i].status_true,
                label: res[i].true_label,
              },
              edges
            );
          }
          if (+res[i].status_false !== 0) {
            edges = addEdge(
              {
                id: "" + (new DateObject().unix + i + 5000),
                source: res[i].id,
                sourceHandle: "false",
                target: res[i].status_false,
                label: res[i].false_label,
              },
              edges
            );
          }
        }

        changeEdges(edges);
        changeNodes(nodes);
      });
    }
  }, [campaignId]);

  const value: CampaignContextType = {
    mainDetails,
    moreDetails,
    nodes,
    edges,
    changeMainDetails,
    changeNodes,
    changeEdges,
    onEdgesChange,
    submitDetails,
  };
  return (
    <CampaignContext.Provider {...{ value }}>
      {children}
    </CampaignContext.Provider>
  );
};

export default CampaignContext;

export { CampaignContextProvider };
